import type { ReactNode } from "react"
import type { PreviewAddressAttributes } from "@/schema/search"
import { normalizeUrl } from "@/utils/normalize-url"
import { Badge } from "@ui/shad"
import { Globe, Mail, MapPin, Phone, Tag, type LucideIcon } from "lucide-react"

import { EmailBadgeWithDialog } from "@/components/tables/email-badge-with-dialog"
import { PhoneBadgeWithRingout } from "@/components/tables/phone-badge-with-ringout"

export function PreviewField({
  icon: Icon,
  label,
  children,
  wrap = false,
  center = false,
}: {
  icon: LucideIcon
  label: string
  children?: ReactNode
  wrap?: boolean
  center?: boolean
}) {
  const empty = children == null || children === ""
  return (
    <div className={`flex gap-1.5 py-0.5 ${center ? "items-center" : "items-start"}`}>
      <span className={`text-muted-foreground shrink-0 ${center ? "" : "mt-0.5"}`} title={label}>
        <Icon className="size-3" aria-label={label} />
      </span>
      <div className={`min-w-0 flex-1 text-xs ${wrap ? "break-words whitespace-pre-wrap" : "truncate"}`}>
        {empty ? <span className="text-muted-foreground">-</span> : children}
      </div>
    </div>
  )
}

export function PreviewContactFields({
  email,
  phone,
  extension,
  name,
  householdId,
  showEmpty = true,
}: {
  email?: string | null
  phone?: string | null
  extension?: string | null
  name: string
  householdId?: string
  showEmpty?: boolean
}) {
  return (
    <>
      {(email || showEmpty) && (
        <PreviewField icon={Mail} label="Email" center>
          {email ? <EmailBadgeWithDialog email={email} householdId={householdId} /> : null}
        </PreviewField>
      )}
      {(phone || showEmpty) && (
        <PreviewField icon={Phone} label="Phone" center>
          {phone ? <PhoneBadgeWithRingout value={phone} extension={extension} name={name} /> : null}
        </PreviewField>
      )}
    </>
  )
}

export function PreviewAddress({
  address,
  showEmpty = true,
}: {
  address: PreviewAddressAttributes
  showEmpty?: boolean
}) {
  const location = [[address.city, address.state].filter(Boolean).join(", "), address.zip].filter(Boolean).join(" ")
  const fullAddress = [address.street_address, location].filter(Boolean).join(", ")
  if (!fullAddress && !showEmpty) return null
  return (
    <PreviewField icon={MapPin} label="Address">
      {fullAddress ? (
        <div className="flex min-w-0" title={fullAddress}>
          {address.street_address && <span className="truncate">{address.street_address}</span>}
          {location && (
            <span className="shrink-0 whitespace-pre">
              {address.street_address ? ", " : ""}
              {location}
            </span>
          )}
        </div>
      ) : null}
    </PreviewField>
  )
}

export function PreviewTags({ tags = [], count = tags.length }: { tags?: string[]; count?: number }) {
  return (
    <PreviewField icon={Tag} label="Tags" center>
      {tags.length ? (
        <div className="flex min-w-0 gap-0.5 overflow-hidden">
          {tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="min-w-0" title={tag}>
              <span className="truncate">{tag}</span>
            </Badge>
          ))}
          {count > 3 && (
            <Badge variant="secondary" className="shrink-0">
              +{count - 3}
            </Badge>
          )}
        </div>
      ) : null}
    </PreviewField>
  )
}

export function PreviewWebsite({ url }: { url?: string }) {
  return (
    <PreviewField icon={Globe} label="Website">
      {url ? (
        <a
          href={normalizeUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-link inline-block max-w-full truncate rounded-md px-1 py-0.5 align-middle transition-colors"
          title={url}
        >
          {url}
        </a>
      ) : null}
    </PreviewField>
  )
}
